import { isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    LOCALE_ID,
    PLATFORM_ID,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { ApiConnectorConfiguration } from '@wasmachen/api-connector';
import { appRoutes } from 'app/app.routes';
import { providePostHogErrorHandler } from 'app/core/errors/posthog-error-handler';
import { resolveInitialLanguage } from 'app/core/i18n/language';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { preflightSessionRefresh } from './core/auth/supertokens-bootstrap';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { twoFactorInterceptor } from './core/two-factor/two-factor.interceptor';

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'dd.MM.yyyy',
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'dd.MM.yyyy',
        monthYearA11yLabel: 'LLLL yyyy',
    },
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptors([twoFactorInterceptor])),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        {
            provide: ApiConnectorConfiguration,
            useValue: new ApiConnectorConfiguration({
                basePath: environment.supertokens.apiDomain,
            }),
        },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        // German locale for Material Date components
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'de-AT',
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: MY_DATE_FORMATS,
        },
        // Global German locale for pipes (date, number, currency)
        { provide: LOCALE_ID, useValue: 'de-AT' },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'de',
                fallbackLang: 'de',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const availableLangs = translocoService
                .getAvailableLangs()
                .map((lang) => (typeof lang === 'string' ? lang : lang.id));
            const lang = resolveInitialLanguage(
                availableLangs,
                translocoService.getDefaultLang()
            );
            translocoService.setActiveLang(lang);

            return firstValueFrom(translocoService.load(lang));
        }),
        provideAppInitializer(() => {
            const platformId = inject(PLATFORM_ID);

            if (!isPlatformBrowser(platformId)) {
                return;
            }

            return preflightSessionRefresh();
        }),

        // PostHog error tracking
        providePostHogErrorHandler(),

        // Fuse
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'modern',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
